/**
 * SchemaMappingOnboardingTour — iter-120 (Feb 2026)
 *
 * One-time onboarding nudge that explains canonical mapping the very
 * first time a user opens the Schema mapping tab. Backed by
 * `user.ui_prefs.seen_schema_mapping_onboarding` so the flag follows
 * the user across browsers/devices (localStorage alone is insufficient).
 *
 * Rendered as a soft primary-tinted card with:
 *   - One-line hook ("Map your columns to a canonical model…")
 *   - 3-bullet 30-second mini-tour
 *   - "Got it — don't show again" dismiss button
 *
 * If the dismiss PATCH fails we still hide the card locally so the
 * user isn't blocked; the flag will re-surface on their next login.
 */
import React from "react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { BookOpen, X, Sparkles, Database, Wand2, Save } from "lucide-react";


export default function SchemaMappingOnboardingTour() {
    const { user, setUser } = useAuth();
    const seen = !!user?.ui_prefs?.seen_schema_mapping_onboarding;
    const [dismissing, setDismissing] = React.useState(false);
    if (seen) return null;

    const dismiss = async () => {
        setDismissing(true);
        const nextPrefs = { ...(user?.ui_prefs || {}), seen_schema_mapping_onboarding: true };
        try {
            const r = await api.patch("/auth/me/ui-prefs", { seen_schema_mapping_onboarding: true });
            const fresh = r.data?.ui_prefs || nextPrefs;
            setUser((u) => (u ? { ...u, ui_prefs: fresh } : u));
            try {
                const raw = localStorage.getItem("analyzio_user");
                if (raw) {
                    const cached = JSON.parse(raw);
                    cached.ui_prefs = fresh;
                    localStorage.setItem("analyzio_user", JSON.stringify(cached));
                }
            } catch { /* harmless */ }
        } catch {
            // Soft-fail: hide locally — flag reappears next login.
            setUser((u) => (u ? { ...u, ui_prefs: nextPrefs } : u));
        } finally {
            setDismissing(false);
        }
    };

    return (
        <div className="border border-primary/40 bg-primary/5 rounded-sm p-4 mb-4 relative"
             data-testid="schema-mapping-onboarding-tour">
            <button
                type="button"
                onClick={dismiss}
                disabled={dismissing}
                className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center rounded-sm hover:bg-primary/10 text-muted-foreground hover:text-foreground disabled:opacity-40"
                data-testid="schema-mapping-onboarding-close"
                aria-label="Dismiss onboarding"
            >
                <X className="h-3.5 w-3.5" />
            </button>
            <div className="flex items-start gap-3 pr-6">
                <BookOpen className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">
                        Map your columns to a canonical model — power any matching dashboard
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                        A canonical model is a shared shape (e.g. <span className="font-mono">model.sales</span>) every
                        dashboard understands. Once you map this dataset, any dashboard bound to that model
                        will start using your rows automatically.
                    </div>
                    <ol className="mt-3 space-y-1.5 text-xs text-foreground/90">
                        <li className="flex items-start gap-2">
                            <Sparkles className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                            <span>
                                <span className="font-semibold">Pick a model</span> below — we auto-suggest the
                                closest fit, or you can choose any model from the dropdown.
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <Database className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                            <span>
                                <span className="font-semibold">Drop your columns into the canonical fields</span>{" "}
                                — required fields are marked, optional fields can be left blank.
                            </span>
                        </li>
                        <li className="flex items-start gap-2">
                            <Wand2 className="h-3 w-3 text-primary mt-0.5 shrink-0" />
                            <span>
                                Stuck? Click <span className="font-mono">Suggest with Sense AI</span> to auto-fill
                                the obvious mappings — review, edit, then{" "}
                                <Save className="inline h-2.5 w-2.5 -mt-0.5" /> Save.
                            </span>
                        </li>
                    </ol>
                    <div className="mt-3 flex items-center gap-2">
                        <Button
                            size="sm" variant="default"
                            onClick={dismiss}
                            disabled={dismissing}
                            className="h-7 rounded-sm text-[11px]"
                            data-testid="schema-mapping-onboarding-dismiss"
                        >
                            {dismissing ? "Saving…" : "Got it — don't show again"}
                        </Button>
                        <span className="text-[10px] text-muted-foreground font-mono">
                            ~30s tour · stored to your profile
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
